import React from 'react';
import { MapPin, Mail, Phone, Printer, Home, MessageCircle } from 'lucide-react';

interface ContactInfo {
  title: string;
  value: string;
  icon: React.ReactNode;
}

interface PhoneContact {
  department: string;
  number: string;
}

const ContactPage: React.FC = () => {
  const phoneContacts: PhoneContact[] = [
    { department: "Reception", number: "05946-292491" },
    { department: "Director/Admin", number: "+91-9997182574" },
    { department: "Manager", number: "+91-9897032340" },
    { department: "Principal", number: "+91-8958851161" },
    { department: "Vice Principal", number: "+91-9997208173" }
  ];

  const contactInfo: ContactInfo[] = [
    {
      title: "Our Location",
      value: "Evergreen Sr. Sec. School Sri Ganga Vihar Colony, Beri Parao, Nainital (Uttarakhand)",
      icon: <MapPin className="w-6 h-6 text-emerald-600" />
    },
    {
      title: "Email Address",
      value: "evergreenpublicschool42@yahoo.in",
      icon: <Mail className="w-6 h-6 text-emerald-600" />
    },
    {
      title: "Fax No",
      value: "(05946) 367624",
      icon: <Printer className="w-6 h-6 text-emerald-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
     

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent mb-4">
            Contact
          </h1>
          <h2 className="text-3xl font-semibold text-gray-700 mb-6">
            Get In Touch
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-blue-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-3 bg-emerald-50 rounded-xl">
                    {info.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {info.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {info.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Phone Numbers */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <div className="flex items-center space-x-3 mb-8">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <Phone className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-800">
                Phone Numbers
              </h3>
            </div>
            
            <div className="space-y-4">
              {phoneContacts.map((contact, index) => (
                <div 
                  key={index}
                  className="flex justify-between items-center p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl hover:from-emerald-100 hover:to-blue-100 transition-all duration-200 group"
                >
                  <span className="font-medium text-gray-700 group-hover:text-gray-800 transition-colors">
                    {contact.department}
                  </span>
                  <span className="text-emerald-600 font-semibold group-hover:text-emerald-700 transition-colors">
                    {contact.number}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Connect?
            </h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              We're here to help with any questions about admissions, academics, or school activities. 
              Don't hesitate to reach out through any of the contact methods above.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-emerald-600 px-8 py-3 rounded-xl font-semibold hover:bg-emerald-50 transition-all duration-200 hover:scale-105 shadow-lg">
                Call Reception
              </button>
              <button className="bg-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-all duration-200 hover:scale-105 shadow-lg border-2 border-white/20">
                Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;